import Kitsu from "kitsu";
import mapValues from "lodash/mapValues";
import pickBy from "lodash/pickBy";

type JsonApiHeaders = Record<string, string>;

type JsonApiBody = Record<string, unknown>;

type JsonApiResult = Promise<JsonApiResponse<any, any>>;

export interface JsonApiParams {
  fields?: Record<string, string[] | string | undefined>;
  include?: string[] | string;
  filter?: Record<string, string> | FilterParams;
  sort?: string;
  page?: {
    page?: number;
    per_page: number;
  };
}

interface JsonApiRequestParams {
  fields?: Record<string, string>;
  include?: string;
  filter?: Record<string, string> | FilterParams;
  sort?: string;
  page?: {
    page?: number;
    per_page: number;
  };
}

export interface JsonApiResponse<T, E = Record<string, string[]>> {
  data: T;
  headers: Record<string, string>;
  meta?: JSONAPIResponseMeta<E>;
}

export interface JSONAPIResponseMeta<E = Record<string, string[]>> {
  [key: string]: any;
  errors?: E;
  page?: number;
  per_page?: number;
  total_pages?: number;
  total_count?: number;
}

interface JsonApiOptions {
  accessToken: string;
  currentOrganisationId?: string;
  baseUrl?: string | null;
}

interface JsonApiRequestOptions {
  params?: JsonApiParams;
  headers?: any;
  axiosOptions?: any;
}

export interface FilterParams {
  [key: string]: any;
  property_id?: string;
  portfolio_id?: string;
  user_id?: string;
  date?: string;
  status?: string;
  state?: string;
  source_method?: string;
}

export interface JsonApiResponseError<T> {
  errors?: JsonApiAttributeErrors<T>;
  code: string;
  message: string;
}

export interface JsonApiError {
  id: string;
  title: string;
}

export type JsonApiAttributeErrors<T> = Partial<
  Record<
    Extract<keyof Omit<T, "id" | "created_at" | "updated_at">, string>,
    string
  >
>;

const formatParamsValue = (value: string | string[]) => {
  if (!value || typeof value === "string") {
    return value;
  }

  return value.join(",");
};

const formatParams = (params?: JsonApiParams) => {
  if (!params) {
    return undefined;
  }

  const { fields, include, ...otherParams } = params;

  const filteredFields = pickBy(fields, (value) => Boolean(value)) as Record<
    string,
    string
  >;
  const formattedFields = mapValues(
    filteredFields,
    (value: string | string[]) => formatParamsValue(value)
  );
  const formattedInclude = include && formatParamsValue(include);

  const formattedParams: JsonApiRequestParams = {
    ...otherParams,
  };

  if (formattedFields && Object.keys(formattedFields).length) {
    formattedParams.fields = formattedFields;
  }

  if (formattedInclude) {
    formattedParams.include = formattedInclude;
  }

  return formattedParams;
};

// kitsu sends empty arrays and objects with an id as relationships
const toKitsuBody = (body: unknown) => {
  if (!body || typeof body !== "object") {
    return body as JsonApiBody | null;
  }

  const { id, type, ...fields } = body as JsonApiBody;
  const attributes: JsonApiBody = {};
  const relationships: JsonApiBody = {};

  for (const [key, value] of Object.entries(fields)) {
    if ((value as { data?: unknown } | null)?.data !== undefined) {
      relationships[key] = value;
    } else {
      attributes[key] = value;
    }
  }

  return { id, type, $: { attributes, relationships } };
};

// kitsu drops attributes named type, and Letterbox sends the correspondence channel in one
const keepChannel = (resource?: {
  type?: string;
  attributes?: JsonApiBody;
}) => {
  if (resource?.type === "correspondence" && resource.attributes?.type) {
    resource.attributes.channel = resource.attributes.type;
  }
};

export const groupErrors = (errors: JsonApiError[]) => {
  const grouped: Record<string, string[]> = {};

  for (const { id, title } of errors) {
    if (!grouped[id]) {
      grouped[id] = [];
    }

    grouped[id].push(title);
  }

  return Object.fromEntries(
    Object.entries(grouped).map(([k, v]) => [k, v.join(", ")])
  );
};

export default class JsonApi {
  private kitsu: Kitsu;

  constructor({ accessToken, currentOrganisationId, baseUrl }: JsonApiOptions) {
    const headers: JsonApiHeaders = {
      Authorization: `Bearer ${accessToken}`,
    };
    if (currentOrganisationId) {
      headers["Organisation-Id"] = currentOrganisationId;
    }

    this.kitsu = new Kitsu({
      headers,
      baseURL: baseUrl ?? `${process.env.NEXT_PUBLIC_FOUNDATIONS_HOST}/v1`,
      pluralize: false,
      camelCaseTypes: false,
    });

    this.kitsu.interceptors.response.use((resp) => {
      [resp.data?.data, resp.data?.included].flat().forEach(keepChannel);

      return {
        ...resp,
        headers: {
          ...resp.headers,
          "status-code": resp.status.toString(),
        },
      };
    });
  }

  get(path: string, options?: JsonApiRequestOptions) {
    const { params, ...otherOptions } = options || {};
    // Kitsu is strict on the page params here so we use any as our API uses page and per_page
    return this.kitsu.get(path, {
      params: formatParams(params),
      ...otherOptions,
    } as any) as JsonApiResult;
  }

  post<T>(
    path: string,
    body: T | null = null,
    options?: JsonApiRequestOptions
  ) {
    const { params, ...otherOptions } = options || {};
    return this.kitsu.create(path, toKitsuBody(body), {
      params: formatParams(params),
      ...otherOptions,
    }) as JsonApiResult;
  }

  patch<T>(path: string, body: T, options?: JsonApiRequestOptions) {
    const { params, ...otherOptions } = options || {};
    const { id } = body as JsonApiBody;
    // kitsu only adds the id and type for paths without "/"
    return this.kitsu.update(
      id ? `${path}/${id}` : path,
      toKitsuBody({ type: path.split("/").pop(), ...body }),
      {
        params: formatParams(params),
        ...otherOptions,
      }
    ) as JsonApiResult;
  }

  delete<T>(path: string, id: T, options?: JsonApiRequestOptions) {
    const { params, ...otherOptions } = options || {};
    return this.kitsu.remove(path, id as unknown as string, {
      params: formatParams(params),
      ...otherOptions,
    }) as JsonApiResult;
  }

  request<T>(
    path: string,
    type: string,
    body: T | null = null,
    method?: string,
    options?: JsonApiRequestOptions
  ) {
    const { params, ...otherOptions } = options || {};
    return this.kitsu.request({
      url: path,
      body: toKitsuBody(body),
      method,
      type,
      params: formatParams(params),
      ...otherOptions,
    }) as JsonApiResult;
  }
}
